import { useCallback, useLayoutEffect, useMemo, useState } from 'react';

export type AIPresence = 'quiet' | 'assistive' | 'prominent';
export type Density = 'airy' | 'default' | 'dense';
export type Surface = 'paper' | 'parchment' | 'slate';

export interface EditorTweaks {
  aiPresence: AIPresence;
  density: Density;
  surface: Surface;
}

export const TWEAK_DEFAULTS: EditorTweaks = {
  aiPresence: 'assistive',
  density: 'default',
  surface: 'paper',
};

export interface SurfaceTheme {
  label: string;
  vars: Record<string, string>;
  docFont: string;
}

export const SURFACE_THEMES: Record<Surface, SurfaceTheme> = {
  paper: {
    label: 'Paper',
    vars: {
      '--ed-bg': 'var(--color-warm-bg)',
      '--ed-surface': 'var(--color-paper)',
      '--ed-surface-2': 'var(--color-paper-2)',
      '--ed-border': 'var(--color-warm-border)',
      '--ed-border-strong': 'var(--color-warm-border-strong)',
      '--ed-text': 'var(--color-ink)',
      '--ed-text-2': 'var(--color-ink-2)',
      '--ed-text-3': 'var(--color-ink-3)',
      '--ed-coral': 'var(--color-coral)',
      '--ed-coral-hover': 'var(--color-coral-hover)',
      '--ed-coral-light': 'var(--color-coral-light)',
      '--ed-coral-dark': 'var(--color-coral-dark)',
      '--ed-teal': 'var(--color-teal)',
      '--ed-teal-light': 'var(--color-teal-light)',
      '--ed-teal-dark': 'var(--color-teal-dark)',
    },
    docFont: "'Inter', system-ui, sans-serif",
  },
  parchment: {
    label: 'Parchment',
    vars: {
      '--ed-bg': '#F4ECDD',
      '--ed-surface': '#FBF6EC',
      '--ed-surface-2': '#EFE3CC',
      '--ed-border': '#D9C9A6',
      '--ed-border-strong': '#BFA77A',
      '--ed-text': '#2C1F10',
      '--ed-text-2': '#6B5A3D',
      '--ed-text-3': '#9A8866',
      '--ed-coral': '#B5421F',
      '--ed-coral-hover': '#963517',
      '--ed-coral-light': '#F6E1D3',
      '--ed-coral-dark': '#7A2810',
      '--ed-teal': '#3F7B6E',
      '--ed-teal-light': '#E8F0EC',
      '--ed-teal-dark': '#2C5A50',
    },
    docFont: "'Lora', Georgia, serif",
  },
  slate: {
    label: 'Slate',
    vars: {
      '--ed-bg': '#0F1419',
      '--ed-surface': '#1A2128',
      '--ed-surface-2': '#232C36',
      '--ed-border': '#2E3A48',
      '--ed-border-strong': '#3E4D5E',
      '--ed-text': '#F1F5F9',
      '--ed-text-2': '#94A3B8',
      '--ed-text-3': '#64748B',
      '--ed-coral': '#FB923C',
      '--ed-coral-hover': '#F97316',
      '--ed-coral-light': '#3D2A1F',
      '--ed-coral-dark': '#FDBA74',
      '--ed-teal': '#5EEAD4',
      '--ed-teal-light': '#1E2F2D',
      '--ed-teal-dark': '#5EEAD4',
    },
    docFont: "'Inter', system-ui, sans-serif",
  },
};

export const DENSITY_VARS: Record<Density, Record<string, string>> = {
  airy: {
    '--ed-pad-card': '18px',
    '--ed-gap-rail': '14px',
    '--ed-doc-fs': '17px',
    '--ed-doc-lh': '1.85',
    '--ed-header-pad': '10px 16px',
  },
  default: {
    '--ed-pad-card': '12px',
    '--ed-gap-rail': '10px',
    '--ed-doc-fs': '15px',
    '--ed-doc-lh': '1.7',
    '--ed-header-pad': '7px 14px',
  },
  dense: {
    '--ed-pad-card': '9px',
    '--ed-gap-rail': '7px',
    '--ed-doc-fs': '14px',
    '--ed-doc-lh': '1.55',
    '--ed-header-pad': '5px 12px',
  },
};

const STORAGE_KEY = 'editor-tweaks-v1';

const ALL_CSS_VAR_KEYS = [
  ...Object.keys(SURFACE_THEMES.paper.vars),
  ...Object.keys(DENSITY_VARS.default),
];

function loadFromStorage(): EditorTweaks {
  if (typeof window === 'undefined') return TWEAK_DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return TWEAK_DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<EditorTweaks>;
    return {
      aiPresence:
        parsed.aiPresence === 'quiet' ||
        parsed.aiPresence === 'assistive' ||
        parsed.aiPresence === 'prominent'
          ? parsed.aiPresence
          : TWEAK_DEFAULTS.aiPresence,
      density:
        parsed.density === 'airy' ||
        parsed.density === 'default' ||
        parsed.density === 'dense'
          ? parsed.density
          : TWEAK_DEFAULTS.density,
      surface:
        parsed.surface === 'paper' ||
        parsed.surface === 'parchment' ||
        parsed.surface === 'slate'
          ? parsed.surface
          : TWEAK_DEFAULTS.surface,
    };
  } catch {
    return TWEAK_DEFAULTS;
  }
}

export interface UseEditorTweaksReturn {
  tweaks: EditorTweaks;
  setTweak: <K extends keyof EditorTweaks>(key: K, value: EditorTweaks[K]) => void;
  /** Inline-style object exposing the active theme + density CSS vars. Spread onto the doc surface for scoped overrides. */
  surfaceStyle: React.CSSProperties;
  /** docFont string for the active surface. */
  docFont: string;
}

/**
 * Owns AI presence / density / surface tweaks and side-effects them onto the
 * document. CSS vars land on `:root`, `data-surface` on `<body>`,
 * `data-ai-presence` on `:root` so descendant `.ed-suggestion` rules cascade.
 * Hydrates from localStorage in a lazy initializer + useLayoutEffect to avoid
 * a flash of default-paper before the persisted surface paints.
 */
export function useEditorTweaks(): UseEditorTweaksReturn {
  const [tweaks, setTweaks] = useState<EditorTweaks>(() => loadFromStorage());

  const setTweak = useCallback(
    <K extends keyof EditorTweaks>(key: K, value: EditorTweaks[K]) => {
      setTweaks((prev) => {
        const next = { ...prev, [key]: value };
        try {
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch {
          /* localStorage may be unavailable (private mode, quota) — non-fatal */
        }
        return next;
      });
    },
    [],
  );

  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const theme = SURFACE_THEMES[tweaks.surface];
    const dens = DENSITY_VARS[tweaks.density];
    Object.entries(theme.vars).forEach(([k, v]) => root.style.setProperty(k, v));
    Object.entries(dens).forEach(([k, v]) => root.style.setProperty(k, v));
    root.dataset.aiPresence = tweaks.aiPresence;
    document.body.dataset.surface = tweaks.surface;
    const prevBg = document.body.style.background;
    document.body.style.background = theme.vars['--ed-bg'];
    return () => {
      ALL_CSS_VAR_KEYS.forEach((k) => root.style.removeProperty(k));
      delete root.dataset.aiPresence;
      delete document.body.dataset.surface;
      document.body.style.background = prevBg;
    };
  }, [tweaks.aiPresence, tweaks.density, tweaks.surface]);

  const surfaceStyle = useMemo<React.CSSProperties>(() => {
    const theme = SURFACE_THEMES[tweaks.surface];
    const dens = DENSITY_VARS[tweaks.density];
    return { ...theme.vars, ...dens } as React.CSSProperties;
  }, [tweaks.surface, tweaks.density]);

  const docFont = SURFACE_THEMES[tweaks.surface].docFont;

  return { tweaks, setTweak, surfaceStyle, docFont };
}
